import type { LakebaseClient } from "./lakebase.js";

export interface DatabricksManagedResourceRecord {
  resourceType: string;
  resourceId: string;
  fingerprint: string;
  createdBy: string;
  toolCallId: string;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, unknown>;
}

export interface DatabricksManagedResourceStore {
  get(
    resourceType: string,
    resourceId: string,
  ): Promise<DatabricksManagedResourceRecord | undefined>;
  put(record: DatabricksManagedResourceRecord): Promise<void>;
  delete(resourceType: string, resourceId: string): Promise<void>;
  list(resourceType?: string): Promise<DatabricksManagedResourceRecord[]>;
}

export class DatabricksManagedResourceConflictError extends Error {
  readonly resourceType: string;
  readonly resourceId: string;
  readonly expectedFingerprint: string;
  readonly actualFingerprint: string;

  constructor(input: {
    resourceType: string;
    resourceId: string;
    expectedFingerprint: string;
    actualFingerprint: string;
  }) {
    super(
      `Databricks managed ${input.resourceType} ${input.resourceId} changed: expected fingerprint ${input.expectedFingerprint}, found ${input.actualFingerprint}.`,
    );
    this.name = "DatabricksManagedResourceConflictError";
    this.resourceType = input.resourceType;
    this.resourceId = input.resourceId;
    this.expectedFingerprint = input.expectedFingerprint;
    this.actualFingerprint = input.actualFingerprint;
  }
}

export class DatabricksUnmanagedResourceError extends Error {
  readonly resourceType: string;
  readonly resourceId: string;

  constructor(resourceType: string, resourceId: string) {
    super(
      `Databricks ${resourceType} ${resourceId} is not recorded as Harness-managed; model-facing mutation is denied.`,
    );
    this.name = "DatabricksUnmanagedResourceError";
    this.resourceType = resourceType;
    this.resourceId = resourceId;
  }
}

/** In-process store for tests and single-process development. Production callers should inject persistence. */
export class MemoryDatabricksManagedResourceStore implements DatabricksManagedResourceStore {
  private readonly records = new Map<string, DatabricksManagedResourceRecord>();

  async get(
    resourceType: string,
    resourceId: string,
  ): Promise<DatabricksManagedResourceRecord | undefined> {
    const record = this.records.get(resourceKey(resourceType, resourceId));
    return record ? structuredClone(record) : undefined;
  }

  async put(record: DatabricksManagedResourceRecord): Promise<void> {
    validateRecord(record);
    this.records.set(resourceKey(record.resourceType, record.resourceId), structuredClone(record));
  }

  async delete(resourceType: string, resourceId: string): Promise<void> {
    this.records.delete(resourceKey(resourceType, resourceId));
  }

  async list(resourceType?: string): Promise<DatabricksManagedResourceRecord[]> {
    return [...this.records.values()]
      .filter((record) => resourceType === undefined || record.resourceType === resourceType)
      .sort((left, right) =>
        resourceKey(left.resourceType, left.resourceId).localeCompare(
          resourceKey(right.resourceType, right.resourceId),
        ),
      )
      .map((record) => structuredClone(record));
  }
}

export interface LakebaseDatabricksManagedResourceStoreOptions {
  client: LakebaseClient;
  /** SQL table name. Identifiers only; defaults to `fabric_harness_managed_resources`. */
  table?: string;
}

/** Durable managed-resource manifest backed by Lakebase or ordinary PostgreSQL. */
export class LakebaseDatabricksManagedResourceStore implements DatabricksManagedResourceStore {
  private readonly table: string;
  private initialized: Promise<void> | undefined;

  constructor(private readonly options: LakebaseDatabricksManagedResourceStoreOptions) {
    this.table = sqlIdentifier(options.table ?? "fabric_harness_managed_resources");
  }

  async get(
    resourceType: string,
    resourceId: string,
  ): Promise<DatabricksManagedResourceRecord | undefined> {
    await this.ensureTable();
    const result = await this.options.client.query<ManagedResourceRow>(
      `SELECT resource_type, resource_id, fingerprint, created_by, tool_call_id, created_at, updated_at, metadata
         FROM ${this.table}
        WHERE resource_type = $1 AND resource_id = $2`,
      [resourceType, resourceId],
    );
    return result.rows[0] ? rowToRecord(result.rows[0]) : undefined;
  }

  async put(record: DatabricksManagedResourceRecord): Promise<void> {
    validateRecord(record);
    await this.ensureTable();
    await this.options.client.query(
      `INSERT INTO ${this.table}
         (resource_type, resource_id, fingerprint, created_by, tool_call_id, created_at, updated_at, metadata)
       VALUES ($1, $2, $3, $4, $5, $6::timestamptz, $7::timestamptz, $8::jsonb)
       ON CONFLICT (resource_type, resource_id) DO UPDATE SET
         fingerprint = EXCLUDED.fingerprint,
         tool_call_id = EXCLUDED.tool_call_id,
         updated_at = EXCLUDED.updated_at,
         metadata = EXCLUDED.metadata`,
      [
        record.resourceType,
        record.resourceId,
        record.fingerprint,
        record.createdBy,
        record.toolCallId,
        record.createdAt,
        record.updatedAt,
        JSON.stringify(record.metadata ?? {}),
      ],
    );
  }

  async delete(resourceType: string, resourceId: string): Promise<void> {
    await this.ensureTable();
    await this.options.client.query(
      `DELETE FROM ${this.table} WHERE resource_type = $1 AND resource_id = $2`,
      [resourceType, resourceId],
    );
  }

  async list(resourceType?: string): Promise<DatabricksManagedResourceRecord[]> {
    await this.ensureTable();
    const result = await this.options.client.query<ManagedResourceRow>(
      `SELECT resource_type, resource_id, fingerprint, created_by, tool_call_id, created_at, updated_at, metadata
         FROM ${this.table}
        ${resourceType === undefined ? "" : "WHERE resource_type = $1"}
        ORDER BY resource_type, resource_id`,
      resourceType === undefined ? [] : [resourceType],
    );
    return result.rows.map(rowToRecord);
  }

  private ensureTable(): Promise<void> {
    this.initialized ??= this.options.client
      .query(
        `CREATE TABLE IF NOT EXISTS ${this.table} (
          resource_type text NOT NULL,
          resource_id text NOT NULL,
          fingerprint text NOT NULL,
          created_by text NOT NULL,
          tool_call_id text NOT NULL,
          created_at timestamptz NOT NULL,
          updated_at timestamptz NOT NULL,
          metadata jsonb NOT NULL DEFAULT '{}'::jsonb,
          PRIMARY KEY (resource_type, resource_id)
        )`,
      )
      .then(() => undefined)
      .catch((error) => {
        this.initialized = undefined;
        throw error;
      });
    return this.initialized;
  }
}

export async function databricksResourceFingerprint(value: unknown): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(canonicalJson(value)),
  );
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function requireDatabricksManagedResource(input: {
  store: DatabricksManagedResourceStore;
  resourceType: string;
  resourceId: string;
  expectedFingerprint?: string;
}): Promise<DatabricksManagedResourceRecord> {
  const record = await input.store.get(input.resourceType, input.resourceId);
  if (!record) throw new DatabricksUnmanagedResourceError(input.resourceType, input.resourceId);
  if (input.expectedFingerprint && record.fingerprint !== input.expectedFingerprint) {
    throw new DatabricksManagedResourceConflictError({
      resourceType: input.resourceType,
      resourceId: input.resourceId,
      expectedFingerprint: input.expectedFingerprint,
      actualFingerprint: record.fingerprint,
    });
  }
  return record;
}

function validateRecord(record: DatabricksManagedResourceRecord): void {
  for (const [name, value] of Object.entries({
    resourceType: record.resourceType,
    resourceId: record.resourceId,
    fingerprint: record.fingerprint,
    createdBy: record.createdBy,
    toolCallId: record.toolCallId,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
  })) {
    if (!value.trim()) throw new Error(`Databricks managed-resource ${name} cannot be empty.`);
  }
  if (
    !/^\d{4}-\d{2}-\d{2}T/.test(record.createdAt) ||
    !/^\d{4}-\d{2}-\d{2}T/.test(record.updatedAt)
  ) {
    throw new Error("Databricks managed-resource timestamps must be ISO-8601 strings.");
  }
}

function resourceKey(resourceType: string, resourceId: string): string {
  return `${resourceType.trim()}\u0000${resourceId.trim()}`;
}

interface ManagedResourceRow {
  resource_type: string;
  resource_id: string;
  fingerprint: string;
  created_by: string;
  tool_call_id: string;
  created_at: string | Date;
  updated_at: string | Date;
  metadata: Record<string, unknown> | string | null;
}

function rowToRecord(row: ManagedResourceRow): DatabricksManagedResourceRecord {
  return {
    resourceType: row.resource_type,
    resourceId: row.resource_id,
    fingerprint: row.fingerprint,
    createdBy: row.created_by,
    toolCallId: row.tool_call_id,
    createdAt: timestamp(row.created_at),
    updatedAt: timestamp(row.updated_at),
    ...optionalMetadata(row.metadata),
  };
}

function timestamp(value: string | Date): string {
  return value instanceof Date ? value.toISOString() : new Date(value).toISOString();
}

function optionalMetadata(value: ManagedResourceRow["metadata"]): {
  metadata?: Record<string, unknown>;
} {
  if (typeof value === "string") {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? { metadata: parsed as Record<string, unknown> }
      : {};
  }
  return value ? { metadata: value } : {};
}

function sqlIdentifier(value: string): string {
  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(value))
    throw new Error("Databricks managed-resource table must be one unqualified SQL identifier.");
  return `"${value}"`;
}

function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== "object") {
    const encoded = JSON.stringify(value);
    return encoded === undefined ? "null" : encoded;
  }
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  return `{${Object.entries(value as Record<string, unknown>)
    .filter(([, child]) => child !== undefined)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, child]) => `${JSON.stringify(key)}:${canonicalJson(child)}`)
    .join(",")}}`;
}
